// src/services/searchService.js
import { collection, query, getDocs, startAfter, limit, doc, getDoc, where, addDoc, serverTimestamp, updateDoc, count, orderBy, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { normalizeText, tokenizeName } from '../utils/textUtils';
import { calculateUnifiedScore, checkDistributedMatches } from '../utils/scoringUtils';
import { generateVerificationId, saveSearchHistory } from '../utils/verificationUtils';
import { checkSearchLimit, incrementSearchUsage } from './usageService';


// Constants
const BATCH_SIZE = 500; // Number of documents to fetch per batch
const MIN_SCORE_THRESHOLD = 45; // Minimum score to include in results (Low confidence and above)

/**
 * Get the total count of active sanctions in the database
 * @returns {Promise<number>} The total count of active sanctions
 */
export const getActiveSanctionsCount = async () => {
  try {
    // Get the count from the stats document
    const statsDocRef = doc(db, 'system', 'sanctions-stats');
    const statsDoc = await getDoc(statsDocRef);
    
    if (statsDoc.exists() && statsDoc.data().activeCount) {
      return statsDoc.data().activeCount;
    }
    
    // Fallback to counting if stats aren't available
    console.log('Sanctions stats document not found or invalid, counting in real-time...');
    const sanctionsRef = collection(db, 'sanctions');
    const countQuery = query(sanctionsRef, where("status", "==", "active"));
    const snapshot = await getDocs(countQuery);
    
    // Store the count for future use
    try {
      await setDoc(statsDocRef, { 
        activeCount: snapshot.size,
        lastUpdated: new Date().toISOString()
      }, { merge: true });
    } catch (err) {
      console.error('Failed to save sanctions count to stats document:', err);
    }
    
    return snapshot.size;
  } catch (error) {
    console.error('Error getting active sanctions count:', error);
    return 6169; // Fallback to the previous hardcoded value
  }
};

/**
 * Search for an entity across sanctions lists
 * @param {string} searchTerm The search term to look for
 * @param {Function} updateProgress Callback to update search progress
 * @param {string} orgId The ID of the organization performing the search (optional)
 * @param {string} userId The ID of the user performing the search (optional)
 * @returns {Object} Search results and statistics
 */
export const searchSanctions = async (searchTerm, updateProgress, orgId = null, userId = null) => {
  try {
    // Check search limits before performing search
    if (orgId) {
      const limitCheck = await checkSearchLimit(orgId);
      if (!limitCheck.allowed) {
        const error = new Error(limitCheck.error || `Monthly search limit exceeded. Used: ${limitCheck.used}/${limitCheck.totalLimit}`);
        error.code = 'SEARCH_LIMIT_EXCEEDED';
        error.limitInfo = limitCheck;
        throw error;
      }
    }

    // Generate a timestamp for the search
    const searchTimestamp = Date.now();
    
    // Generate verification ID
    const verificationId = await generateVerificationId(searchTerm, searchTimestamp);
    
    // Normalize the search term
    const searchTermNormalized = normalizeText(searchTerm);
    
    // Tokenize the search term
    const searchTermTokens = tokenizeName(searchTerm);
    
    // Stats tracking
    const stats = {
      totalDocumentsQueried: 0,
      totalNamesProcessed: 0,
      totalBatches: 0,
      skippedRecords: 0
    };
    
    // Get the total count of active sanctions
    const totalRecords = await getActiveSanctionsCount();
    
    // Initialize progress with the total count
    if (updateProgress) {
      updateProgress({
        processed: 0,
        total: totalRecords
      });
    }
    
    // Array to store all matching results
    const matchingResults = [];
    
    // Implementation of pagination to fetch ALL records
    let hasMoreData = true;
    let lastDoc = null;
    
    // Log function for progress
    const updateLoadingProgress = (processed) => {
      console.log(`Processed ${processed} records of approximately ${totalRecords}`);
      if (updateProgress) {
        updateProgress({ 
          processed: processed, 
          total: totalRecords 
        });
      }
    };
    
    // Pagination loop
    while (hasMoreData) {
      stats.totalBatches++;
      
      // Create a reference to the sanctions collection
      const sanctionsRef = collection(db, 'sanctions');
      
      // Construct query with pagination and active status filter
      let batchQuery;
      if (lastDoc) {
        batchQuery = query(sanctionsRef, where("status", "==", "active"), orderBy('__name__'), startAfter(lastDoc), limit(BATCH_SIZE));
      } else {
        batchQuery = query(sanctionsRef, where("status", "==", "active"), orderBy('__name__'), limit(BATCH_SIZE));
      }
      
      const querySnapshot = await getDocs(batchQuery);
      
      // Check if we've reached the end
      hasMoreData = querySnapshot.size === BATCH_SIZE;
      
      // Update last document for pagination
      if (hasMoreData && querySnapshot.size > 0) {
        lastDoc = querySnapshot.docs[querySnapshot.size - 1];
      }
      
      // Update stats
      stats.totalDocumentsQueried += querySnapshot.size;
      
      // Update progress with the current processed count and dynamic total
      updateLoadingProgress(stats.totalDocumentsQueried);
      
      // Process documents to find matches
      const documents = [];
      
      // First pass: process each document and check for distributed matches
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        
        // Skip records without names
        if (!data.names || !data.names.length) {
          stats.skippedRecords++;
          return;
        }
        
        // Check for distributed name matching
        const distributedMatch = checkDistributedMatches(data, searchTermTokens);
        
        // Store document with distributed match information
        documents.push({
          id: doc.id,
          data,
          distributedMatch
        });
      });
      
      // Second pass: process individual names
      documents.forEach((doc) => {
        const data = doc.data;
        
        // Create a base record with common information
        const baseRecord = {
          id: doc.id,
          docData: data,
          remark: data.remark || '',
          designationDetails: data.designationDetails || '',
          subjectType: data.subjectType?.code || '',
          citizenships: (data.citizenships || []).map(c => c.countryDescription).join(', '),
          birthdates: (data.birthdates || []).map(b => b.birthdate || 
            `${b.year || ''}${b.year && (b.dayOfMonth || b.monthOfYear) ? '-' : ''}${b.monthOfYear || ''}${b.monthOfYear && b.dayOfMonth ? '-' : ''}${b.dayOfMonth || ''}`
          ).filter(Boolean).join(', '),
          isDistributedMatch: doc.distributedMatch.isDistributedMatch,
          distributionInfo: doc.distributedMatch.distribution,
          matchedTokens: doc.distributedMatch.matchedTokens
        };
        
        // Highest scoring name variant
        let bestMatch = null;
        let bestScore = 0;
        
        // Process each name within the document
        data.names.forEach((name) => {
          stats.totalNamesProcessed++;
          
          // Get full name
          const constructedName = `${name.title || ''} ${name.firstName || ''} ${name.middleName || ''} ${name.lastName || ''}`.trim();
          const fullName = name.wholeName ? name.wholeName : constructedName;
          
          if (!fullName) return;
          
          const normalizedName = normalizeText(fullName);
          
          const nameEntry = {
            ...baseRecord,
            nameIndex: data.names.indexOf(name),
            fullName: fullName,
            normalizedName: normalizedName,
            firstName: name.firstName || '',
            middleName: name.middleName || '',
            lastName: name.lastName || '',
            gender: name.gender || '',
            function: name.function || '',
            title: name.title || '',
            strong: name.strong || false,
            qualityLevel: name.qualityLevel || 'UNKNOWN',
            qualityScore: name.qualityScore || 0,
            qualityFlags: name.qualityFlags || []
          };
          
          // Calculate unified score
          const score = calculateUnifiedScore(nameEntry, searchTermTokens, searchTermNormalized);
          nameEntry.unifiedScore = score;
          
          // Set confidence level
          if (score >= 95) {
            nameEntry.confidenceLevel = "Very High";
          } else if (score >= 80) {
            nameEntry.confidenceLevel = "High";
          } else if (score >= 65) {
            nameEntry.confidenceLevel = "Medium";
          } else if (score >= 45) {
            nameEntry.confidenceLevel = "Low";
          } else {
            nameEntry.confidenceLevel = "Very Low";
          }
          
          // Track best matching name variant
          if (score > bestScore) {
            bestScore = score;
            bestMatch = nameEntry;
          }
        });
        
        // If we have a match above threshold, add to results
        if (bestMatch && bestMatch.unifiedScore >= MIN_SCORE_THRESHOLD) {
          matchingResults.push(bestMatch);
        }
      });
      
      // Process distributed matches separately to ensure they're not filtered out
      const distributedMatches = [];

      documents.forEach((doc) => {
        if (doc.distributedMatch?.isDistributedMatch) {
          // Find the best name variant for this distributed match
          let bestNameEntry = null;
          let bestScore = 0;
          
          doc.data.names.forEach((name) => {
            // Get wholeName
            const fullName = name.wholeName || '';
            if (!fullName) return;
            
            // Create a name entry for scoring
            const nameEntry = {
              id: doc.id,
              docData: doc.data,
              nameIndex: doc.data.names.indexOf(name),
              fullName: fullName,
              normalizedName: normalizeText(fullName),
              remark: doc.data.remark || '',
              designationDetails: doc.data.designationDetails || '',
              subjectType: doc.data.subjectType?.code || '',
              citizenships: (doc.data.citizenships || []).map(c => c.countryDescription).join(', '),
              birthdates: (doc.data.birthdates || []).map(b => b.birthdate || 
                `${b.year || ''}${b.year && (b.dayOfMonth || b.monthOfYear) ? '-' : ''}${b.monthOfYear || ''}${b.monthOfYear && b.dayOfMonth ? '-' : ''}${b.dayOfMonth || ''}`
              ).filter(Boolean).join(', '),
              isDistributedMatch: true,
              distributionInfo: doc.distributedMatch.distribution,
              matchedTokens: doc.distributedMatch.matchedTokens
            };
            
            // Calculate base score
            const score = calculateUnifiedScore(nameEntry, searchTermTokens, searchTermNormalized);
            
            // Apply distributed match boost
            const distributedBoost = 25; // Higher boost since all name components are important
            const boostedScore = Math.min(100, score + distributedBoost);
            
            nameEntry.unifiedScore = boostedScore;
            nameEntry.distributedBoost = distributedBoost;
            
            // Set confidence level
            if (boostedScore >= 95) {
              nameEntry.confidenceLevel = "Very High";
            } else if (boostedScore >= 80) {
              nameEntry.confidenceLevel = "High";
            } else if (boostedScore >= 65) {
              nameEntry.confidenceLevel = "Medium";
            } else if (boostedScore >= 45) {
              nameEntry.confidenceLevel = "Low";
            } else {
              nameEntry.confidenceLevel = "Very Low";
            }
            
            // Track best match
            if (boostedScore > bestScore) {
              bestScore = boostedScore;
              bestNameEntry = nameEntry;
            }
          });
          
          // Add distributed matches with scores above threshold
          if (bestNameEntry && bestNameEntry.unifiedScore >= MIN_SCORE_THRESHOLD) {
            distributedMatches.push(bestNameEntry);
          }
        }
      });
      
      // Add distributed matches to results
      matchingResults.push(...distributedMatches);
    }
    
    // Sort results by score (highest first)
    matchingResults.sort((a, b) => b.unifiedScore - a.unifiedScore);
    
    // Limit results to reasonable number
    let limitedResults = matchingResults;
    if (matchingResults.length > 200) {
      console.log(`Limited ${matchingResults.length} results to top 200 by score`);
      limitedResults = matchingResults.slice(0, 200); // Limit to top 200 scores
    }
    
    // Group results by confidence level for stats
    const veryHighConfidence = matchingResults.filter(r => r.unifiedScore >= 95);
    const highConfidence = matchingResults.filter(r => r.unifiedScore >= 80 && r.unifiedScore < 95);
    const mediumConfidence = matchingResults.filter(r => r.unifiedScore >= 65 && r.unifiedScore < 80);
    const lowConfidence = matchingResults.filter(r => r.unifiedScore >= 45 && r.unifiedScore < 65);
    const veryLowConfidence = []; // No longer showing Very Low confidence results
    
    // Final stats
    const searchStats = {
      ...stats,
      totalMatches: matchingResults.length,
      veryHighConfidence: veryHighConfidence.length,
      highConfidence: highConfidence.length,
      mediumConfidence: mediumConfidence.length,
      lowConfidence: lowConfidence.length,
      veryLowConfidence: veryLowConfidence.length,
      distributedMatches: matchingResults.filter(r => r.isDistributedMatch).length,
      verificationId: verificationId,
      searchTime: new Date().toISOString(),
      totalInDatabase: totalRecords
    };
    
        // Save search history if organization and user IDs are provided
    if (orgId && userId) {
      try {
        await saveSearchHistory({
          orgId,
          userId,
          searchTerm,
          stats: searchStats,
          results: limitedResults,
          verificationId
        });
      } catch (historyError) {
        console.error("Error saving search history:", historyError);
        // Don't fail the search if history saving fails
      }
    }

    // Increment search usage after successful search
    if (orgId) {
      try {
        await incrementSearchUsage(orgId, 1);
        console.log(`Incremented search usage for organization ${orgId}`);
      } catch (usageError) {
        console.error("Error incrementing search usage:", usageError);
        // Don't fail the search if usage increment fails
      }
    }

    return {
      results: limitedResults,
      stats: searchStats
    };
    
  } catch (error) {
    console.error('Error searching sanctions:', error);
    throw error;
  }
};

/**
 * Get verification record by ID
 * @param {string} verificationId - The verification ID to retrieve
 * @param {string} orgId - The ID of the organization requesting the verification
 * @returns {Promise<Object|null>} - The verification data or null if not found
 */
export const getVerification = async (verificationId, orgId) => {
  try {
    console.log('Fetching verification record:', verificationId, 'for organization:', orgId);
    
    // Ensure orgId is provided
    if (!orgId) {
      console.error('getVerification called without orgId');
      throw new Error('Organization ID is required to fetch verification data.');
    }

    // First, try querying the sanction-searches collection with verificationId in different fields
    const searchesRef = collection(db, 'sanction-searches');
    
    // Query for verificationId in the document directly, ensuring organization owns it
    const directQuery = query(
      searchesRef, 
      where('verificationId', '==', verificationId), 
      where('orgId', '==', orgId) // *** UPDATED TO USE orgId ***
    );
    let querySnapshot = await getDocs(directQuery);
    
    // If not found, try looking for verificationId in stats object, ensuring organization owns it
    if (querySnapshot.empty) {
      console.log('Verification ID not found directly, checking stats.verificationId field...');
      const statsQuery = query(
        searchesRef, 
        where('stats.verificationId', '==', verificationId),
        where('orgId', '==', orgId) // *** UPDATED TO USE orgId ***
      );
      querySnapshot = await getDocs(statsQuery);
    }
    
    // If we found the verification ID in sanction-searches, return it
    if (!querySnapshot.empty) {
      // Get the first matching document from sanction-searches
      const searchDoc = querySnapshot.docs[0];
      const searchData = searchDoc.data();
      
      console.log('Found verification document:', searchDoc.id, {
        hasVerificationId: !!searchData.verificationId,
        hasStatsVerificationId: !!searchData.stats?.verificationId,
        matches: searchData.results?.length || searchData.matches?.length || 0,
      });
      
      // Determine which results array to use (support both field names)
      const resultsArray = searchData.results || searchData.matches || [];
      
      // Results are returned as-is from the search data
      // Review status is now handled separately via the review service
      const results = resultsArray;
      
      // Return the search data with additional fields
      return {
        ...searchData,
        verificationId: verificationId,
        searchId: searchData.searchId || searchDoc.id, // Ensure searchId is available
        results,
        searchTerm: searchData.searchTerm || searchData.name || '',
        stats: {
          ...(searchData.stats || {}),
          totalMatches: resultsArray.length,
          verificationId: verificationId
        }
      };
    }
    
    // If not found in sanction-searches directly, check batch-jobs for backward compatibility
    console.log('Verification ID not found directly in sanction-searches, checking batch-jobs...');
    
    // Query all batch jobs (this is inefficient but preserved for backward compatibility)
    const batchJobsRef = collection(db, 'batch-jobs');
    const batchJobsQuery = query(batchJobsRef);
    const batchJobsSnapshot = await getDocs(batchJobsQuery);
    
    // First try looking in old format with results array inside batch jobs
    for (const jobDoc of batchJobsSnapshot.docs) {
      const jobData = jobDoc.data();
      if (jobData.results && Array.isArray(jobData.results)) {
        // Find a result with the matching verification ID
        const matchingResult = jobData.results.find(result => 
          result.verificationId === verificationId
        );
        
        if (matchingResult) {
          console.log('Found verification ID in batch-jobs results (old format)');
          
          // Return details as-is from the search data
          // Review status is now handled separately via the review service
          const details = matchingResult.details || [];
          
          // Format the data to match the expected structure
          return {
            verificationId,
            searchId: matchingResult.searchId || jobDoc.id, // Ensure searchId is available
            searchTerm: matchingResult.name,
            results: details,
            stats: {
              totalMatches: matchingResult.matches || 0,
              totalHighConfidenceMatches: matchingResult.highConfidenceMatches || 0,
              verificationId: verificationId
            }
          };
        }
      }
    }
    
    // If still not found, try looking for a mass-upload source search in sanction-searches
    console.log('Checking for mass-upload searches in sanction-searches...');
    const massUploadQuery = query(
      searchesRef, 
      where('source', '==', 'mass-upload'),
      where('orgId', '==', orgId) // *** UPDATED TO USE orgId ***
      // Note: We might still need to match on verificationId within the results here if the top-level IDs don't match
    );
    
    const massUploadSnapshot = await getDocs(massUploadQuery);
    
    // Check each mass upload document for the verification ID in various locations
    for (const doc of massUploadSnapshot.docs) {
      const data = doc.data();
      
      // Check all possible locations for the verification ID
      const docVerificationId = data.verificationId;
      const statsVerificationId = data.stats?.verificationId;
      const resultVerificationId = (data.results || []).find(r => r.verificationId === verificationId)?.verificationId;
      
      if (docVerificationId === verificationId || statsVerificationId === verificationId || resultVerificationId === verificationId) {
        console.log('Found verification ID in mass-upload source:', doc.id);
        
        // Determine which results array to use
        const resultsArray = data.results || data.matches || [];
        
        // Return results as-is from the search data
        // Review status is now handled separately via the review service
        const results = resultsArray;
        
        return {
          ...data,
          verificationId,
          searchId: data.searchId || doc.id, // Ensure searchId is available
          results,
          searchTerm: data.searchTerm || data.name || '',
          stats: {
            ...(data.stats || {}),
            totalMatches: resultsArray.length,
            verificationId: verificationId,
            batchJobId: data.batchJobId // Include batch job ID
          }
        };
      }
    }
    
    console.error('Verification record not found in any collection:', verificationId);
    throw new Error(`Verification record not found in any collection: ${verificationId}`);
  } catch (error) {
    console.error('Error fetching verification record:', error);
    throw error;
  }
};

/**
 * Save search results to the database
 * @param {Object} searchData - Data to save
 * @param {string} searchData.searchTerm - The search term
 * @param {Array} searchData.results - The search results
 * @param {Object} searchData.stats - Search statistics
 * @param {string} searchData.orgId - ID of the organization
 * @param {string} searchData.userId - ID of the user who performed the search
 * @param {string} searchData.timestamp - ISO timestamp of when the search was performed
 * @returns {Promise<string>} - ID of the saved search
 */
export const saveSearchResults = async (searchData) => {
  try {
    console.log("saveSearchResults called with:", { 
      searchTerm: searchData.searchTerm,
      resultsCount: searchData.results?.length,
      orgId: searchData.orgId ? 'present' : 'missing',
      userId: searchData.userId ? 'present' : 'missing'
    });
    
    // Don't save if there's no organization or user ID
    if (!searchData.orgId || !searchData.userId) {
      console.log('Cannot save search results: No organization or user ID provided');
      return null;
    }
    
    // Create a new document in the sanction-searches collection
    const searchesRef = collection(db, 'sanction-searches');
    
    // Clean the data to remove undefined values
    const cleanObject = (obj) => {
      if (!obj || typeof obj !== 'object') return obj;
      
      // Handle arrays
      if (Array.isArray(obj)) {
        return obj.map(item => cleanObject(item)).filter(item => item !== undefined);
      }
      
      // Handle objects
      const cleanedObj = {};
      Object.entries(obj).forEach(([key, value]) => {
        // Skip undefined values
        if (value === undefined) return;
        
        // Recursively clean nested objects
        cleanedObj[key] = cleanObject(value);
      });
      
      return cleanedObj;
    };
    
    // Note: Review status is now handled separately in the result-reviews collection
    // No need to add review fields to search results anymore
    
    // Clean the search data
    const cleanedData = cleanObject(searchData);
    
    // Add a server timestamp
    const docData = {
      ...cleanedData,
      searchTimestampUTC: serverTimestamp(),
      savedAt: new Date().toISOString(),
      source: 'individual' // Add source field to indicate this is an individual search
    };
    
    console.log("Adding document to Firestore collection 'sanction-searches'");
    
    // Add the document to Firestore
    const docRef = await addDoc(searchesRef, docData);
    console.log('Search results saved with ID:', docRef.id);
    
    // Create a case for this search if it has results
    if (searchData.results && searchData.results.length > 0) {
      try {
        // Import here to avoid circular dependency
        const { createCase } = await import('./caseService');
        await createCase({
          searchTerm: searchData.searchTerm,
          searchId: docRef.id,
          orgId: searchData.orgId,
          userId: searchData.userId,
          verificationId: searchData.stats?.verificationId,
          stats: searchData.stats,
          totalMatches: searchData.results.length,
          source: 'individual',
          entityType: searchData.entityType
        });
      } catch (caseError) {
        console.error('Error creating case:', caseError);
        // Don't fail the search if case creation fails
      }
    }
    
    return docRef.id;
  } catch (error) {
    console.error('Error saving search results:', error);
    return null;
  }
};

/**
 * Get search results by ID
 * @param {string} searchId - The ID of the saved search
 * @param {string} orgId - ID of the organization (for authorization)
 * @returns {Promise<Object|null>} - The search data or null if not found
 */
export const getSearchResultsById = async (searchId, orgId) => {
  try {
    console.log('Fetching search results by ID:', searchId);
    
    // Get the document from Firestore
    const docRef = doc(db, 'sanction-searches', searchId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      console.log('Search results not found');
      return null;
    }
    
    const data = docSnap.data();
    
    // Ensure only the organization that created the search can access it
    if (data.orgId !== orgId) {
      console.error('Unauthorized access attempt to search results');
      return null;
    }
    
    return {
      id: searchId,
      searchTerm: data.searchTerm,
      results: data.results || [],
      stats: data.stats || {},
      timestamp: data.searchTimestampUTC?.toDate() || new Date(data.timestamp || data.savedAt)
    };
  } catch (error) {
    console.error('Error fetching search results by ID:', error);
    return null;
  }
};

// Note: updateSearchResultReviewStatus function has been removed
// Review status updates are now handled by the reviewService

/**
 * @deprecated Use reviewService.getReviewCounts() and caseService.updateCaseStatus() instead
 * Get the overall case status based on the review status of all results
 * This function is kept for backward compatibility but should not be used for new code
 * @param {Array} results - The array of search results with review statuses
 * @returns {string} - The overall case status: 'pending', 'completed', or 'partially-reviewed'
 */
export const getCaseStatus = (results) => {
  console.warn('getCaseStatus is deprecated. Use reviewService.getReviewCounts() and caseService.updateCaseStatus() instead.');
  
  if (!results || results.length === 0) {
    return 'pending';
  }
  
  // Count review statuses
  const reviewCounts = results.reduce((counts, result) => {
    const status = result.review_status || 'pending';
    counts[status] = (counts[status] || 0) + 1;
    return counts;
  }, {});
  
  // If all are pending, case is pending
  if (reviewCounts.pending === results.length) {
    return 'pending';
  }
  
  // If no pending results, case is completed
  if (!reviewCounts.pending || reviewCounts.pending === 0) {
    return 'completed';
  }
  
  // Otherwise, it's partially reviewed
  return 'partially-reviewed';
};

/**
 * Save an individual batch search result to the search history
 * @param {Object} searchData - The search result data
 * @param {string} searchData.searchTerm - The name that was searched
 * @param {Array} searchData.results - The search results/matches
 * @param {Object} searchData.stats - Search statistics 
 * @param {string} searchData.orgId - Organization ID
 * @param {string} searchData.userId - User who performed the search
 * @param {string} searchData.verificationId - Verification ID for this search
 * @param {string} searchData.batchJobId - ID of the batch job this search is part of
 * @returns {Promise<string>} - ID of the saved search record
 */
export const saveBatchSearchResult = async (searchData) => {
  try {
    console.log("saveBatchSearchResult called for:", searchData.searchTerm);
    
    // Don't save if there's no organization or user ID
    if (!searchData.orgId || !searchData.userId) {
      console.log('Cannot save batch search result: No organization or user ID provided');
      return null;
    }
    
    // Create a new document in the sanction-searches collection
    const searchesRef = collection(db, 'sanction-searches');
    
    // Clean the data to remove undefined values
    const cleanObject = (obj) => {
      if (!obj || typeof obj !== 'object') return obj;
      
      // Handle arrays
      if (Array.isArray(obj)) {
        return obj.map(item => cleanObject(item)).filter(item => item !== undefined);
      }
      
      // Handle objects
      const cleanedObj = {};
      Object.entries(obj).forEach(([key, value]) => {
        // Skip undefined values
        if (value === undefined) return;
        
        // Recursively clean nested objects
        cleanedObj[key] = cleanObject(value);
      });
      
      return cleanedObj;
    };
    
    // Note: Review status is now handled separately in the result-reviews collection
    // No need to add review fields to search results anymore
    
    // Clean the search data
    const cleanedData = cleanObject(searchData);
    
    // Add a server timestamp and source indicator
    const docData = {
      ...cleanedData,
      source: 'mass-upload', // Indicate this came from a batch job
      batchJobId: searchData.batchJobId, // Store the batch job ID for reference
      searchTimestampUTC: serverTimestamp(),
      savedAt: new Date().toISOString()
    };
    
    console.log("Adding batch search result to Firestore collection 'sanction-searches'");
    
    // Add the document to Firestore
    const docRef = await addDoc(searchesRef, docData);
    console.log('Batch search result saved with ID:', docRef.id);
    
    // Create a case for this batch search if it has results
    if (searchData.results && searchData.results.length > 0) {
      try {
        // Import here to avoid circular dependency
        const { createCase } = await import('./caseService');
        await createCase({
          searchTerm: searchData.searchTerm,
          searchId: docRef.id,
          orgId: searchData.orgId,
          userId: searchData.userId,
          verificationId: searchData.verificationId,
          stats: searchData.stats,
          totalMatches: searchData.results.length,
          source: 'mass-upload',
          batchJobId: searchData.batchJobId,
          entityType: searchData.entityType
        });
      } catch (caseError) {
        console.error('Error creating case for batch search:', caseError);
        // Don't fail the search if case creation fails
      }
    }
    
    return docRef.id;
  } catch (error) {
    console.error('Error saving batch search result:', error);
    return null;
  }
};